<template>
  <div
    ref="virtualSelectRoot"
    class="se-multiple-select-wrap el-select se-multiple-se virtual-select"
    :class="{ 'is-disabled': disabled, 'is-open': dropdownVisible }"
  >
      <div
        class="el-input el-input--suffix el-input--mini"
        @click="!disabled && toggleDropdown()"
      >
        <div class="el-input__inner">
          <div class="el-select__tags" v-if="innerValue.length">
            <el-tag
              v-for="sel in displayTags"
              :key="sel.SEID"
              type="info"
              size="mini"
              closable
              disable-transitions
              @close.stop="removeValue(sel.SEID)"
            >
              {{ sel.Name }}
            </el-tag>
          </div>
          <span
            v-else
            class="el-select__placeholder"
          >
            {{ placeholder }}
          </span>
          <!-- 占位 input，用于保持与 el-select 一致的高度与对齐 -->
          <input
            class="el-select__input"
            type="text"
            autocomplete="off"
            size="mini"
            style="opacity: 0; width: 0; height: 0; padding: 0; border: none;"
          >
        </div>
        <span class="el-input__suffix">
          <span class="el-input__suffix-inner">
            <i
              class="el-select__caret el-input__icon el-icon-arrow-up"
              :class="{ 'is-reverse': dropdownVisible }"
            ></i>
          </span>
        </span>
      </div>

      <transition name="el-zoom-in-top">
        <div
          v-show="dropdownVisible"
          ref="dropdown"
          class="el-select-dropdown se-multiple-se-dropdown"
          :style="dropdownStyle"
        >
          <div class="filter-input-wrap" v-if="filterable">
            <el-input
              v-model="filterQuery"
              size="small"
              placeholder="Search"
              prefix-icon="el-icon-search"
              clearable
              @click.native.stop
            />
          </div>
          <div class="wrapper" v-if="type == 'quickPush' && desiredCertifiedValuesList.length > 0">
            <el-checkbox :indeterminate="isIndeterminate" v-model="checkAll" @change="handleCheckAllChange"
              :disabled="checkAllDisabled">
              <span class="se-name" :class="{ 'checked': checkAll }">Select All (Certified + Pending certification)</span>
            </el-checkbox>
          </div>
          <div class="wrapper" v-if="type == 'push' && innerOptions.length > 0">
            <el-checkbox :indeterminate="isIndeterminate" v-model="checkAll" @change="handleCheckAllChange"
              :disabled="checkAllDisabled">
              <span class="se-name" :class="{ 'checked': checkAll }">Select All</span>
            </el-checkbox>
          </div>
          <div class="el-select-dropdown__wrap el-scrollbar__wrap" style="max-height: 274px;">
            <div
              v-if="filteredOptions && filteredOptions.length"
              ref="listWrap"
              class="virtual-list-wrap"
            >
              <RecycleScroller
                :key="virtualListKey"
                class="scroller"
                :items="filteredOptions"
                :item-size="52"
                :buffer="800"
                key-field="SEID"
                v-slot="{ item }"
              >
                <div
                  class="multiple-se-item virtual-se-item"
                  :class="{ 'selected': isSelected(item), 'is-disabled': isDisabled(item) }"
                  :title="showTips ? tipTitle(item) : null"
                  @click="onItemClick(item)"
                >
                  <div class="virtual-se-item-left">
                    <span class="vsi-checkbox" :class="{ 'is-checked': isSelected(item) }"></span>
                    <span class="se-name">{{ item.Name }}</span>
                  </div>
                  <div class="virtual-se-item-right">
                    <span v-if="item.types && item.types.length>0" v-role=[1,2,3,13] class="vsi-tag vsi-tag--info" :style="showTag ? 'margin-right:8px' : ''">{{ item.types[0] }}</span>
                    <template v-if="showTag">
                      <span v-if="item.Certified == 2" class="vsi-tag vsi-tag--warning">Pending certification</span>
                      <span v-else-if="item.Certified == 1" class="vsi-tag vsi-tag--success">Certified</span>
                      <span v-else class="vsi-tag vsi-tag--danger">Uncertified</span>
                    </template>
                  </div>
                </div>
              </RecycleScroller>
              <div v-show="listLoading" class="virtual-list-loading">
                <i class="el-icon-loading"></i>
                <span>Loading...</span>
              </div>
            </div>
            <div
              v-else
              class="el-select-dropdown__empty"
            >
              无匹配的数据
            </div>
          </div>
        </div>
      </transition>
  </div>
</template>

<script>
import * as _ from 'lodash'
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

export default {
  name: 'SEMultipleSelect',
  components: { RecycleScroller },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: Array,
    options: Array,
    placeholder: String,
    showTag: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showTips: {
      type: Boolean,
      default: false,
    },
    type: String,
    filterable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      innerOptions: [],
      checkAllList: [],
      checkAllDisabled: false,
      checkAll: false,
      isIndeterminate: false,
      desiredCertifiedValuesList: [],
      dropdownVisible: false,
      filterQuery: '',
      debouncedFilterQuery: '',
      virtualListSeed: 0,
      listLoading: false,
      dropdownStyle: {},
      scrollElRef: null,
      scrollHandlerRef: null,
      hideListLoadingDebounced: null,
      lastScrollTop: 0,
      lastScrollTime: 0,
    }
  },
  created() {
    this.debounceFilter = _.debounce(() => {
      this.debouncedFilterQuery = this.filterQuery

      // 过滤后 RecycleScroller 会重渲染，需要重新绑定滚动事件
      if (!this.dropdownVisible) return
      this.$nextTick(() => {
        setTimeout(() => this.bindListScroll(), 50)
      })
    }, 280)
    this.hideListLoadingDebounced = _.debounce(() => {
      this.listLoading = false
    }, 200)
  },
  watch: {
    options(value) {
      const desiredCertifiedValues = [1, 2]
      this.desiredCertifiedValuesList = value
        .filter(item => desiredCertifiedValues.includes(item.Certified))
        .map(item => item.SEID)
      this.innerOptions = value.map((item) => ({ ...item, isDisabled: !!item.isDisabled }))
    },
    value() {
      this.onSelectChange()
    },
    filterQuery() {
      this.debounceFilter()
    },
    dropdownVisible(val) {
      if (val) {
        this.$nextTick(this.updateDropdownPosition)
        this.filterQuery = ''
        this.debouncedFilterQuery = ''
        this.virtualListSeed += 1
        this.listLoading = false
        document.addEventListener('click', this.handleClickOutside)
        this.$nextTick(() => {
          setTimeout(() => this.bindListScroll(), 150)
        })
      } else {
        this.unbindListScroll()
        this.listLoading = false
        document.removeEventListener('click', this.handleClickOutside)
      }
    },
  },
  computed: {
    innerValue: {
      set(value) {
        this.$emit('change', value)
      },
      get() {
        return this.value
      },
    },
    filteredOptions() {
      const q = (this.debouncedFilterQuery || '').trim().toLowerCase()
      if (!q) return this.innerOptions
      return this.innerOptions.filter((item) => {
        const name = (item.Name || '').toLowerCase()
        // const province = (item.Province || '').toLowerCase()
        // const city = (item.City || '').toLowerCase()
        // const hospital = (item.Hospital || '').toLowerCase()
        // const department = (item.Department || '').toLowerCase()
        // return name.includes(q) || province.includes(q) || city.includes(q) || hospital.includes(q) || department.includes(q)
        return name.includes(q)
      })
    },
    virtualListKey() {
      return 'vl-' + this.virtualListSeed + '-' + (this.debouncedFilterQuery || '')
    },
    selectedSet() {
      return new Set(this.innerValue || [])
    },
    certMap() {
      const m = new Map()
      for (const o of this.innerOptions) m.set(o.SEID, o.Certified)
      return m
    },
    hasUncertifiedSelected() {
      const map = this.certMap
      for (let i = 0; i < (this.innerValue || []).length; i++) {
        if (map.get(this.innerValue[i]) === 0) return true
      }
      return false
    },
    hasCertifiedSelected() {
      const map = this.certMap
      for (let i = 0; i < (this.innerValue || []).length; i++) {
        if (map.get(this.innerValue[i]) === 1) return true
      }
      return false
    },
    hasPendingSelected() {
      const map = this.certMap
      for (let i = 0; i < (this.innerValue || []).length; i++) {
        if (map.get(this.innerValue[i]) === 2) return true
      }
      return false
    },
    itemBySeid() {
      const m = new Map()
      for (const o of this.innerOptions) m.set(o.SEID, o)
      return m
    },
    displayTags() {
      const ids = (this.innerValue || [])
      const byId = this.itemBySeid
      return ids.map((id) => byId.get(id)).filter(Boolean)
    },
    ses() {
      return (certified) => {
        return _.chain(this.innerOptions)
          .filter(({ Certified }) => Certified === certified)
          .map(({ SEID }) => SEID)
          .value()
      }
    },
    isSelectedSE() {
      return (certified) => {
        return _.intersection(this.innerValue, this.ses(certified)).length > 0
      }
    },
  },
  methods: {
    bindListScroll() {
      this.unbindListScroll()
      const wrap = this.$refs.listWrap
      if (!wrap) return
      const scrollEl = wrap.querySelector('.vue-recycle-scroller')
      if (!scrollEl) return
      this.scrollElRef = scrollEl
      this.lastScrollTop = scrollEl.scrollTop || 0
      this.lastScrollTime = Date.now()
      this.scrollHandlerRef = () => {
        const now = Date.now()
        const currentTop = this.scrollElRef ? this.scrollElRef.scrollTop || 0 : 0
        const delta = Math.abs(currentTop - (this.lastScrollTop || 0))
        const dt = now - (this.lastScrollTime || 0)
        this.lastScrollTop = currentTop
        this.lastScrollTime = now

        // 只有“快速滚动”或“滚动幅度很大”时才显示 loading
        const largeJump = delta > 400 // 一次跳动超过约 8 个 item 高度
        const fastScroll = dt > 0 && (delta / dt) > 1.0 // > ~1000px/s

        if (!largeJump && !fastScroll) return

        this.listLoading = true
        this.hideListLoadingDebounced()
      }
      scrollEl.addEventListener('scroll', this.scrollHandlerRef, { passive: true })
    },
    unbindListScroll() {
      if (this.scrollElRef && this.scrollHandlerRef) {
        this.scrollElRef.removeEventListener('scroll', this.scrollHandlerRef)
        this.scrollElRef = null
        this.scrollHandlerRef = null
      }
    },
    removeValue(id) {
      if (this.disabled) return
      const ids = this.innerValue || []
      const next = ids.filter((v) => v !== id)
      this.innerValue = next
      this.onSelectChange()
    },
    emitSelectChange() {
      this.$emit('select-change', {
        value: this.innerValue || [],
        isCertified: this.hasCertifiedSelected,
        isUncertified: this.hasUncertifiedSelected,
        isPendingcertified: this.hasPendingSelected,
      })
    },
    isSelected(item) {
      return item && this.selectedSet.has(item.SEID)
    },
    isDisabled(item) {
      if (!item) return true
      const selected = this.selectedSet.has(item.SEID)
      if (this.hasUncertifiedSelected) return !selected
      if (this.hasCertifiedSelected || this.hasPendingSelected) return item.Certified === 0
      return !!item.isDisabled
    },
    tipTitle(item) {
      if (!item) return ''
      const parts = [
        `Name：${item.Name || ''}`,
        `Province：${item.Province || ''}`,
        `City：${item.City || ''}`,
        `Hospital/Institution：${item.Hospital || ''}`,
        `Department：${item.Department || ''}`,
      ]
      return parts.join('\n')
    },
    toggleDropdown() {
      this.dropdownVisible = !this.dropdownVisible
    },
    handleClickOutside(e) {
      const root = this.$refs.virtualSelectRoot
      const dropdown = this.$refs.dropdown
      if (root && dropdown && !root.contains(e.target) && !dropdown.contains(e.target)) {
        this.dropdownVisible = false
      }
    },
    updateDropdownPosition() {
      if (!this.$refs.virtualSelectRoot) return
      const rect = this.$refs.virtualSelectRoot.getBoundingClientRect()
      this.dropdownStyle = {
        position: 'fixed',
        left: rect.left + 'px',
        top: (rect.bottom + 4) + 'px',
        minWidth: rect.width + 'px',
        zIndex: 2000,
      }
    },
    onItemClick(item) {
      if (this.isDisabled(item)) return
      const ids = this.innerValue || []
      const has = this.selectedSet.has(item.SEID)
      const next = has ? ids.filter((id) => id !== item.SEID) : ids.concat(item.SEID)
      this.innerValue = next
      this.onSelectChange()
    },
    handleCheckAllChange(val) {
      if (this.type == 'quickPush') {
        const desiredCertifiedValues = [1, 2]
        this.innerValue = val ? this.innerOptions
          .filter(item => desiredCertifiedValues.includes(item.Certified))
          .map(item => item.SEID) : []
        this.checkAll = val
        this.isIndeterminate = false
        this.$nextTick(() => {
          this.$emit('select-change', {
            value: this.innerValue,
            isCertified: this.hasCertifiedSelected,
            isUncertified: this.hasUncertifiedSelected,
            isPendingcertified: this.hasPendingSelected,
          })
        })
      }

      if (this.type == 'push') {
        this.innerValue = val ? this.innerOptions.map(item => item.SEID) : []
        this.checkAll = val
        this.isIndeterminate = false
      }
    },
    onSelectChange() {
      if (this.type == 'quickPush') {
        if (this.innerValue.length == 0) {
          this.isIndeterminate = false
          this.checkAll = false
        } else if (this.innerValue.length < this.desiredCertifiedValuesList.length) {
          this.isIndeterminate = true
        } else {
          this.isIndeterminate = false
          this.checkAll = true
        }
        if (this.hasUncertifiedSelected) {
          this.checkAllDisabled = true
          this.isIndeterminate = false
        } else {
          this.checkAllDisabled = false
        }
      }
      if (this.type == 'push') {
        if (this.innerValue.length == 0) {
          this.isIndeterminate = false
          this.checkAll = false
        } else if (this.innerValue.length < this.innerOptions.length) {
          this.isIndeterminate = true
        } else {
          this.isIndeterminate = false
          this.checkAll = true
        }
      }
      this.$nextTick(this.emitSelectChange)
    },
  },
}
</script>

<style scoped lang="scss">
.se-multiple-select-wrap {
  display: inline-block;
  width: 100%;
}

.virtual-select {
  .el-input__inner {
    min-height: 28px; // mini 尺寸的基础高度
    height: auto;
    padding: 2px 30px 2px 10px;
    line-height: 22px;
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .el-select__tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-left: -4px;
    position: relative;
    transform: translateY(0);
    top: 0;

    .el-tag {
      margin: 2px 4px 2px 4px;
    }
  }

  .el-select__placeholder {
    color: #c0c4cc;
  }
}

.se-multiple-se-dropdown {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(233, 233, 233, 0.877);
  background: #fff;
  padding: 4px 0;
}

.virtual-list-wrap {
  position: relative;
  height: 274px;
  background: #fff;
}
.virtual-list-loading {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 1);
  color: #d52b1e;
  font-size: 14px;
}
.virtual-list-loading .el-icon-loading {
  margin-right: 6px;
  font-size: 18px;
}
.scroller {
  height: 100%;
  background: #fff;
}
.scroller ::v-deep .vue-recycle-scroller__item-wrapper {
  background: #fff;
}

.virtual-se-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 52px;
  padding: 0 20px;
  cursor: pointer;
  box-sizing: border-box;
  background: #fff;
  contain: layout style paint;

  .virtual-se-item-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .virtual-se-item-right {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  span.se-name {
    margin-left: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .vsi-checkbox {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 1px solid #dcdfe6;
    border-radius: 2px;
    flex-shrink: 0;
    position: relative;
  }
  .vsi-checkbox.is-checked {
    background-color: #d52b1e;
    border-color: #d52b1e;
  }
  .vsi-checkbox.is-checked::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 0px;
    width: 4px;
    height: 8px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  .vsi-tag {
    display: inline-block;
    padding: 0 8px;
    font-size: 12px;
    line-height: 20px;
    border-radius: 4px;
    margin-right: 4px;
  }
  .vsi-tag--info {
    background: #f4f4f5;
    color: #909399;
    border: 1px solid #e9e9eb;
  }
  .vsi-tag--warning {
    background: #fdf6ec;
    color: #e6a23c;
    border: 1px solid #f5dab1;
  }
  .vsi-tag--success {
    background: #f0f9eb;
    color: #67c23a;
    border: 1px solid #c2e7b0;
  }
  .vsi-tag--danger {
    background: #fef0f0;
    color: #f56c6c;
    border: 1px solid #fbc4c4;
  }

  &.selected {
    color: #d52b1e;
  }

  &.is-disabled {
    color: #c0c4cc;
    cursor: not-allowed;
    .vsi-checkbox,
    .vsi-tag {
      opacity: 0.5;
    }
  }

  &:not(.is-disabled):hover {
    background: #f5f7fa;
  }
}

.filter-input-wrap {
  padding: 8px;
  border-bottom: 1px solid #ebeef5;
}

.se-multiple-se {
  .multiple-se-item {
    display: flex;
    justify-content: space-between;
    align-items: center;

    span.se-name {
      margin-left: 10px;
    }

    &.selected {
      &::after {
        content: none !important;
      }

      &.is-disabled {
        color: #f56c6c !important;
        cursor: not-allowed;
      }
    }

    &.is-disabled {

      .el-checkbox,
      .el-tag {
        opacity: 0.5;
      }
    }

    ::v-deep {
      .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
        background-color: #d52b1e !important;
        border-color: #d52b1e !important;

        &::after {
          border-color: #fff !important;
        }
      }

      &:not(.is-disabled) {

        .el-checkbox,
        .el-checkbox__input,
        .el-checkbox__inner {

          &,
          &::after,
          input[type='checkbox'] {
            cursor: pointer !important;
          }
        }

        .el-checkbox__input.is-disabled:not(.is-checked) .el-checkbox__inner {
          background-color: #ffffff !important;
          border-color: #dcdfe6 !important;
        }
      }
    }
  }
}

.checked {
  color: #d52b1e;
  font-weight: bold;
}

.wrapper {
  height: 34px;
  padding-left: 20px;
  display: flex;
  align-items: center;
}
.virtual-select{
  .el-select__tags span{
    display: inline-block !important;
  }
}
</style>
